import { useEffect } from 'react'

export type UiSurfaceIntent = 'create' | 'edit' | 'view'
export type UiSurfaceMode = 'page' | 'canvas' | 'dialog'

/** Current Create / Edit / View object for the agent harness. Values stay out of this payload. */
export type UiSurface = {
  intent: UiSurfaceIntent
  entity: string
  id: string
  mode: UiSurfaceMode
  fields?: string[]
}

let pageSurface: UiSurface | null = null
let canvasSurface: UiSurface | null = null

export function getUiSurface(): UiSurface | null {
  return canvasSurface ?? pageSurface
}

export function setPageUiSurface(surface: UiSurface | null): void {
  pageSurface = surface
}

export function setCanvasUiSurface(surface: UiSurface | null): void {
  canvasSurface = surface
}

export function resetUiSurfaceForTests(): void {
  pageSurface = null
  canvasSurface = null
}

function surfaceKey(surface: UiSurface | null): string {
  if (!surface) return ''
  const fields = (surface.fields || []).join(',')
  return `${surface.intent}|${surface.entity}|${surface.id}|${surface.mode}|${fields}`
}

/** Declare the page-level working object. Canvas overlay (if any) still wins. */
export function useDeclareUiSurface(surface: UiSurface | null): void {
  const key = surfaceKey(surface)
  useEffect(() => {
    if (!key) {
      setPageUiSurface(null)
      return () => setPageUiSurface(null)
    }
    const [intent, entity, id, mode, fieldsJoined] = key.split('|')
    setPageUiSurface({
      intent: intent as UiSurfaceIntent,
      entity,
      id,
      mode: mode as UiSurfaceMode,
      ...(fieldsJoined ? { fields: fieldsJoined.split(',') } : {}),
    })
    return () => setPageUiSurface(null)
  }, [key])
}
